import {
  queryOptions,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import invariant from "tiny-invariant";
import {
  type Address,
  erc20Abi,
  getContract,
  type PublicClient,
  zeroAddress,
} from "viem";
import {
  useAccount,
  useChainId,
  usePublicClient,
  useWatchContractEvent,
} from "wagmi";

import { type QueryOptions } from "../types";

type Params = {
  accountAddress: Address | undefined;
  currencyAddress: Address | undefined;
  contractAddress: Address | undefined;
  pc: PublicClient | undefined;
};
export const getAllowanceQueryKey = ({
  accountAddress,
  currencyAddress,
  contractAddress,
}: Omit<Params, "pc">) => {
  return [
    "use-allowance-query",
    accountAddress,
    currencyAddress,
    contractAddress,
  ];
};

const getQueryOptions = <TData = bigint>(
  params: Params,
  options?: QueryOptions<bigint, unknown, TData>
) => {
  const { accountAddress, contractAddress, currencyAddress, pc } = params;
  return queryOptions<bigint, unknown, TData>({
    queryKey: getAllowanceQueryKey({
      accountAddress,
      contractAddress,
      currencyAddress,
    }),
    queryFn: async () => {
      invariant(pc, "useAllowancesQuery. pc is undefined");
      invariant(accountAddress, "useAllowancesQuery. address is undefined");
      invariant(
        currencyAddress,
        "useAllowancesQuery. currencyAddress is undefined"
      );
      invariant(
        contractAddress,
        "useAllowancesQuery. contractAddress is undefined"
      );

      const contract = getContract({
        abi: erc20Abi,
        address: currencyAddress,
        client: pc,
      });

      return contract.read.allowance([accountAddress, contractAddress]);
    },
    enabled: Boolean(
      accountAddress && pc && contractAddress && currencyAddress
    ),
    ...options,
  });
};

type UseAllowanceQueryParams = {
  currencyAddress: Address | undefined;
  contractAddress: Address | undefined;
};

export const useAllowanceQuery = <TData = bigint>(
  params: UseAllowanceQueryParams,
  options?: QueryOptions<bigint, unknown, TData>
) => {
  const queryClient = useQueryClient();
  const { address: accountAddress } = useAccount();
  const chainId = useChainId();
  const pc = usePublicClient({ chainId });

  const { currencyAddress, contractAddress } = params;

  useWatchContractEvent({
    abi: erc20Abi,
    args: {
      owner: accountAddress,
    },
    eventName: "Approval",
    address: currencyAddress,
    chainId,
    onLogs: (logs) => {
      const log = logs[0];
      const key = getQueryOptions({
        accountAddress,
        contractAddress: log.args.spender ?? zeroAddress,
        currencyAddress,
        pc,
      }).queryKey;

      queryClient.setQueryData(key, log.args.value);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return useQuery(
    getQueryOptions<TData>(
      {
        contractAddress,
        currencyAddress,
        accountAddress,
        pc,
      },
      options
    )
  );
};

type UseAllowancesQueryParams = {
  currencyAddresses: Address[] | undefined;
  contractAddress: Address;
};
export const useAllowancesQueries = <TData = bigint>(
  params: UseAllowancesQueryParams,
  options?: QueryOptions<bigint, unknown, TData>
) => {
  const { contractAddress, currencyAddresses = [] } = params;
  const chainId = useChainId();
  const pc = usePublicClient({ chainId });
  const { address } = useAccount();

  return useQueries({
    queries: currencyAddresses.map((currencyAddress) => {
      return getQueryOptions<TData>(
        {
          contractAddress,
          accountAddress: address,
          currencyAddress,
          pc,
        },
        options
      );
    }),
  });
};
